import Badge from '@pelckmans/elementis/components/badge';
import Card from '@pelckmans/elementis/components/card';
import Pill from '@pelckmans/elementis/components/pill';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseCategory, getExerciseCategories } from '../../../../api/exercise-category';
import { Module } from '../../../../api/module';
import ModuleStatus from '../../../../components/ModuleStatus';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import IdentityContext from '../../../../contexts/IdentityContext';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useRoutes } from '../../../../hooks/useRoutes';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { hasAccessToModule } from '../../../../utils/identity';
import ExerciseModuleNodeTags from './ExerciseModuleNodeTags';
import ModuleCardTags from './ModuleCardTags';

function ExerciseCategories({ categories }: { categories: ExerciseCategory[] }) {
  return categories.map(category => {
    const badgeProps = category.usage > 0 ? { badge: <Badge>{category.usage}</Badge> } : {};

    return (
      <Pill role="listitem" key={category.id} {...badgeProps}>
        {category.name}
      </Pill>
    );
  });
}

export default function ModuleCard({
  module,
  isVirtual = false,
  sharedUserMaterialCount = 0,
  sharedSlideSetCount = 0,
}: {
  module: Module;
  isVirtual?: boolean;
  sharedUserMaterialCount?: number;
  sharedSlideSetCount?: number;
}) {
  const [exerciseCategories, setExerciseCategories] = useState<ExerciseCategory[]>([]);

  const { identity } = useContext(IdentityContext);
  const { buildRelativePath } = useRoutes();
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();

  const { bookType, externalContentUrl, id, isExerciseModule, niceName, publishingState, slug, styling } = module;

  const captureModuleAccessed = async () => {
    await analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.MODULE_ACCESSED,
      objectId: id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SUBMODULE_SELECTION,
    });
  };

  const captureExerciseModuleAccessed = async () => {
    await analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.VIRTUAL_EXERCISE_MODULE_ACCESSED,
      objectId: id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SUBMODULE_SELECTION,
    });
  };

  const fetchExerciseCategories = useCallback(
    async (moduleId: string) => {
      try {
        const { data } = await getExerciseCategories({
          moduleId,
          includeSubModules: true,
        });

        const general = data.find(({ id }) => id === 'general');
        const rest = data.filter(({ id }) => id !== 'general');

        const flattenedThemes = [...(general?.categories?.map(category => ({ ...category, usage: 0 })) || []), ...rest];
        setExerciseCategories(flattenedThemes);
      } catch (e) {
        handleError(e);
      }
    },
    [handleError],
  );

  useEffect(() => {
    if (isVirtual) fetchExerciseCategories(id);
  }, [isVirtual, fetchExerciseCategories, id]);

  if (isVirtual) {
    return (
      <Card
        as="link"
        // This props was invented before EM's existed, so this refers to VEM
        isExerciseModule
        imagePosition="left"
        titleTag="h4"
        titleText={t('PAGES.SUBMODULE_SELECTOR.VIRTUAL_EXERCISE_MODULE.TITLE')}
        subText={t('PAGES.SUBMODULE_SELECTOR.VIRTUAL_EXERCISE_MODULE.SUBTEXT')}
        href={buildRelativePath('MATERIAL.VEM')}
        onClick={captureExerciseModuleAccessed}
        tags={{
          role: 'list',
          children: <ExerciseCategories categories={exerciseCategories} />,
          'aria-label': t('PAGES.SUBMODULE_SELECTOR.VIRTUAL_EXERCISE_MODULE.TAGS.A11Y_LABEL'),
        }}
      />
    );
  }

  const externalProps = Boolean(externalContentUrl)
    ? {
        external: true as const,
        externalAriaLabel: t('PAGES.SUBMODULE_SELECTOR.EXTERNAL.A11Y_LABEL'),
      }
    : {};

  return (
    <Card
      key={id}
      as="link"
      image={styling?.cover || 'placeholder'}
      imagePosition="left"
      titleTag="h4"
      titleText={niceName}
      subText={bookType}
      href={externalContentUrl || buildRelativePath('MATERIAL.CONTENT.TOC', { selectedModuleSlug: slug, nodeId: '' })}
      tags={{
        role: 'list',
        children: isExerciseModule ? (
          <ExerciseModuleNodeTags module={module} />
        ) : (
          <ModuleCardTags
            module={module}
            sharedUserMaterialCount={sharedUserMaterialCount}
            sharedSlideSetCount={sharedSlideSetCount}
          />
        ),
        'aria-label': t('PAGES.SUBMODULE_SELECTOR.TAGS.A11Y_LABEL', {
          moduleName: niceName,
        }),
      }}
      disabled={!hasAccessToModule(identity, publishingState, id)}
      status={{
        children: <ModuleStatus module={module} showUnlicensedStatus />,
        'aria-label': undefined,
        role: undefined,
      }}
      target={externalContentUrl ? '_blank' : undefined}
      onClick={captureModuleAccessed}
      {...externalProps}
    />
  );
}
